import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import TiersPayment from "../TiersPayment";
import { DefaultAdvisorDefinition } from "@PayPlans/helpers/DefaultSections/Advisors";

// MUI
import { Grid } from "@mui/material";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Hooks
import { getDefaultPaymentType } from "@PayPlans/hooks/useSectionsDefaults";

// Constants
const DEFAULT_CONTENT = { COMBINED: ["partsAndLabor"], NOT_COMBINED: ["parts", "labor"] };

const isCombined = (value) => (value ? "COMBINED" : "NOT_COMBINED");

const updatedMappedModel = (model) => {
	const { parts, labor, partsAndLabor, isPartsAndLabor, isGuaranteedDraw } = model || {};
	return {
		parts,
		labor,
		partsAndLabor,
		isPartsAndLabor,
		isGuaranteedDraw,
	};
};

const checkCommissions = (tier) => {
	for (const commission of tier.commissions) {
		if (commission.commission.value > 0) {
			return false;
		}
	}
	return true;
};

const checkDoubleTabInitialState = (model) => {
	let result = true;
	if (model.isPartsAndLabor) {
		for (const tier of model.partsAndLabor.tiers) {
			result &&= checkCommissions(tier);
		}
	} else {
		for (const tier of model.labor.tiers) {
			result &&= checkCommissions(tier);
		}
		for (const tier of model.parts.tiers) {
			result &&= checkCommissions(tier);
		}
	}
	return result;
};

export default function DefaultCommissions(props) {
	const {
		sectionId = "default",
		content = null,
		tabsProps = {
			tabs: ["Parts", "Labor", "Parts & Labor"],
			errors: [],
		},
		isPartsAndLabor = false,
		isGuaranteedDraw = false,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	const { position } = usePayPlan();
	const defaultPaymentType = getDefaultPaymentType(position);

	// Hooks
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors();

	// States
	const [model, setModel] = useState(null);
	const [doubleTabError, setDoubleTabError] = useState(false);

	// Error handler
	const errors = useMemo(() => {
		if (model?.isPartsAndLabor) {
			const errorPartsAndLabor = tabsProps.errors?.includes(getErrorKey(sectionId, "partsAndLabor"));
			return [errorPartsAndLabor || doubleTabError];
		} else {
			const errorParts = tabsProps.errors?.includes(getErrorKey(sectionId, "parts"));
			const errorLabor = tabsProps.errors?.includes(getErrorKey(sectionId, "labor"));
			return [errorParts || doubleTabError, errorLabor || doubleTabError];
		}
	}, [sectionId, tabsProps.errors, getErrorKey, model?.isPartsAndLabor, doubleTabError]);

	// Actions handlers
	const updateAll = (section, data, isValid = true) => {
		setModel({ ...data });
		const tabError = checkDoubleTabInitialState(data);
		setDoubleTabError(tabError);
		onActionUpdate?.(section, null, updatedMappedModel({ ...data }), isValid && !tabError);
	};

	const handleUpdateSwitches = (section, area, value) => {
		const updated = { ...model, [area]: value };
		let isValid = true;
		if (area === "isPartsAndLabor") {
			removeSectionError(section, "parts");
			updated.parts = DefaultAdvisorDefinition(defaultPaymentType);
			removeSectionError(section, "labor");
			updated.labor = DefaultAdvisorDefinition(defaultPaymentType);
			removeSectionError(section, "partsAndLabor");
			updated.partsAndLabor = DefaultAdvisorDefinition(defaultPaymentType);
			isValid = false;
		}
		updateAll(section, updated, isValid);
	};

	const handleUpdateTiers = (section, area, tiers) => {
		const updated = { ...model, [area]: { tiers } };
		updateAll(section, updated);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useEffect(() => {
		const clonedContent = clone(content);
		if (clonedContent) {
			if (!clonedContent?.partsAndLabor || !clonedContent?.partsAndLabor?.tiers?.length) {
				clonedContent.partsAndLabor = DefaultAdvisorDefinition(defaultPaymentType);
			}
			if (clonedContent?.isPartsAndLabor === undefined || clonedContent?.isPartsAndLabor === null) {
				clonedContent.isPartsAndLabor = isPartsAndLabor ?? false;
			}
			if (clonedContent?.isGuaranteedDraw === undefined || clonedContent?.isGuaranteedDraw === null) {
				clonedContent.isGuaranteedDraw = isGuaranteedDraw ?? false;
			}

			const tabError = checkDoubleTabInitialState(clonedContent);
			setDoubleTabError(tabError);
		}
		setModel({
			tabsProps,
			...clonedContent,
		});

		return () => {
			setDoubleTabError(false);
			setModel(null);
		};
	}, [tabsProps, content]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					!!model && (
						<TabSelectable
							tabs={
								(model?.isPartsAndLabor
									? model?.tabsProps.tabs.filter((tab) => tab === "Parts & Labor")
									: model?.tabsProps.tabs.filter((tab) => tab !== "Parts & Labor")) || []
							}
							errors={errors}
							content={DEFAULT_CONTENT[isCombined(model?.isPartsAndLabor)].map((key, index) => {
								const localContent = model[key];
								const { tiers = [] } = localContent || {};

								return (
									<Grid container key={`${key}_${index}`}>
										<Grid item xs={12} className={"models-tiered-area"}>
											<TiersPayment
												tiers={tiers}
												doubleTabError={doubleTabError}
												readOnly={readOnly}
												onActionUpdate={(updatedTiers) => handleUpdateTiers(sectionId, key, updatedTiers)}
												onActionValidation={(valid) => handleValidation(sectionId, key, valid)}
											/>
										</Grid>
									</Grid>
								);
							})}
							topActions={[
								<CustomizedSwitches
									key={"isPartsAndLabor"}
									textLeft={true}
									textChecked={"Combine Parts and Labor"}
									textUnChecked={"Combine Parts and Labor"}
									checked={Boolean(model?.isPartsAndLabor)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "isPartsAndLabor", value)}
									tooltip={{
										title: "Combine Parts and Labor.",
										placement: "bottom",
										key: "default-commissions-guarantee-draw-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"isGuaranteedDraw"}
									textLeft={true}
									textChecked={"Guaranteed Draw"}
									textUnChecked={"Guaranteed Draw"}
									checked={Boolean(model?.isGuaranteedDraw)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "isGuaranteedDraw", value)}
									tooltip={{
										title: "Guarantee the draw.",
										placement: "bottom",
										key: "default-commissions-guarantee-draw-tooltip",
									}}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
DefaultCommissions.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	tabsProps: PropTypes.shape({
		tabs: PropTypes.array,
		errors: PropTypes.array,
	}),
	isPartsAndLabor: PropTypes.bool,
	isGuaranteedDraw: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
